import { Debug, SymbolType } from "@spawn/common";
import {
	IAssetHandle,
	IAssetListHandle,
	SoundResource,
	SpineResource,
	TextureResource,
	asset,
	service,
} from "@spawn/core";
import { GoldenBonusSymbolTypes } from "@spawn/golden-bonus";
import { ITeaserAnimationAddonOptions, TeaserAddon } from "@spawn/slot-machine";
import Victor from "victor";
import { BaseSymbolTypes, SymbolService as CoreSymbolService, MysteryTeaserAddon } from "@spawn/nrg-games-core";

export {
	BaseSymbolTypes,
	convertBraggIdToSymbolTypeId,
	convertCubeIdToSymbolType,
	symbolTypeBraggIds,
	symbolTypeCubeIds,
} from "@spawn/nrg-games-core";
export type { SymbolTypes } from "@spawn/nrg-games-core";

/** Art of the game: asset handles, textures, animations and offsets. */
@service
export class SymbolService extends CoreSymbolService {
	@asset(TextureResource) public readonly symbolLV1!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolLV2!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolLV3!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolLV4!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolHV1!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolHV2!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolHV3!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolHV4!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolTopFull!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolTopCut!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolTopGold!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolTopGoldBig!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolScatter!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolScatterFG!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolMystery!: IAssetHandle<TextureResource>;
	@asset(TextureResource) public readonly symbolMysteryFrame!: IAssetHandle<TextureResource>;

	@asset(SpineResource) public readonly lvAnimation!: IAssetHandle<SpineResource>;
	@asset(SpineResource) public readonly hv1Animation!: IAssetHandle<SpineResource>;
	@asset(SpineResource) public readonly hv2Animation!: IAssetHandle<SpineResource>;
	@asset(SpineResource) public readonly hv3Animation!: IAssetHandle<SpineResource>;
	@asset(SpineResource) public readonly hv4Animation!: IAssetHandle<SpineResource>;
	@asset(SpineResource) public readonly scatterAnimation!: IAssetHandle<SpineResource>;
	@asset(SpineResource) public readonly mysteryAnimation!: IAssetHandle<SpineResource>;

	@asset(SoundResource, { multiple: true }) public readonly scatterTeaserSounds!: IAssetListHandle<SoundResource>;

	public getTextureForSymbolType(symbolType: SymbolType): TextureResource {
		switch (symbolType) {
			case BaseSymbolTypes.LV1:
				return this.symbolLV1();
			case BaseSymbolTypes.LV2:
				return this.symbolLV2();
			case BaseSymbolTypes.LV3:
				return this.symbolLV3();
			case BaseSymbolTypes.LV4:
				return this.symbolLV4();
			case BaseSymbolTypes.HV1:
				return this.symbolHV1();
			case BaseSymbolTypes.HV2:
				return this.symbolHV2();
			case BaseSymbolTypes.HV3:
				return this.symbolHV3();
			case BaseSymbolTypes.HV4:
				return this.symbolHV4();
			case BaseSymbolTypes.Top:
				return this.symbolTopFull();
			case BaseSymbolTypes.Scatter:
				return this.symbolScatter();
			case BaseSymbolTypes.ScatterFG:
				return this.symbolScatterFG();
			case BaseSymbolTypes.Mystery:
				return this.symbolMystery();
			case GoldenBonusSymbolTypes.BonusX1:
				return this.goldenBonusAssetsService.symbolBonus1();
			case GoldenBonusSymbolTypes.BonusX2:
				return this.goldenBonusAssetsService.symbolBonus2();
			case GoldenBonusSymbolTypes.BonusX5:
				return this.goldenBonusAssetsService.symbolBonus5();
			case GoldenBonusSymbolTypes.BonusX10:
				return this.goldenBonusAssetsService.symbolBonus10();
			case GoldenBonusSymbolTypes.BonusX25:
				return this.goldenBonusAssetsService.symbolBonus25();
			case GoldenBonusSymbolTypes.BonusX50:
				return this.goldenBonusAssetsService.symbolBonus50();
			case GoldenBonusSymbolTypes.BonusX100:
				return this.goldenBonusAssetsService.symbolBonus100();
			case GoldenBonusSymbolTypes.BonusX250:
				return this.goldenBonusAssetsService.symbolBonus250();
			case GoldenBonusSymbolTypes.BonusX500:
				return this.goldenBonusAssetsService.symbolBonus500();
			case GoldenBonusSymbolTypes.BonusX1000:
				return this.goldenBonusAssetsService.symbolBonus1000();
			case GoldenBonusSymbolTypes.BonusX2500:
				return this.goldenBonusAssetsService.symbolBonus2500();
			default:
				Debug.fail(`Unknown symbol type: ${symbolType}`);
		}
	}

	public getWinAnimationForSymbolType(symbolType: SymbolType): [string, SpineResource, string] | undefined {
		switch (symbolType) {
			case BaseSymbolTypes.LV1:
				return ["lv1", this.lvAnimation(), "lv"];
			case BaseSymbolTypes.LV2:
				return ["lv2", this.lvAnimation(), "lv"];
			case BaseSymbolTypes.LV3:
				return ["lv3", this.lvAnimation(), "lv"];
			case BaseSymbolTypes.LV4:
				return ["lv4", this.lvAnimation(), "lv"];
			case BaseSymbolTypes.HV1:
				return ["hv1", this.hv1Animation(), "default"];
			case BaseSymbolTypes.HV2:
				return ["hv2", this.hv2Animation(), "default"];
			case BaseSymbolTypes.HV3:
				return ["hv3", this.hv3Animation(), "default"];
			case BaseSymbolTypes.HV4:
				return ["hv4", this.hv4Animation(), "default"];
			case BaseSymbolTypes.Top:
				return ["top_wild", this.goldenBonusAssetsService.goldenTriggerSpine(), "top_wild"];
			case BaseSymbolTypes.Scatter:
				return ["scatter_bg_win", this.scatterAnimation(), "scatter_bg_win"];
			case BaseSymbolTypes.ScatterFG:
				return ["scatter_fg_static", this.scatterAnimation(), "scatter_fg_static"];
			case GoldenBonusSymbolTypes.BonusX1:
				return ["bonus_1_win", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_1_win"];
			case GoldenBonusSymbolTypes.BonusX2:
				return ["bonus_2_win", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_2_win"];
			case GoldenBonusSymbolTypes.BonusX5:
				return ["bonus_5_win", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_5_win"];
			case GoldenBonusSymbolTypes.BonusX10:
				return ["bonus_10_win", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_10_win"];
			case GoldenBonusSymbolTypes.BonusX25:
				return ["bonus_25_win", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_25_win"];
			case GoldenBonusSymbolTypes.BonusX50:
				return ["bonus_50_win", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_50_win"];
			case GoldenBonusSymbolTypes.BonusX100:
				return ["bonus_100_win", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_100_win"];
			case GoldenBonusSymbolTypes.BonusX250:
				return ["bonus_250_win", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_250_win"];
			case GoldenBonusSymbolTypes.BonusX500:
				return ["bonus_500_win", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_500_win"];
			case GoldenBonusSymbolTypes.BonusX1000:
				return ["bonus_1000_win", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_1000_win"];
			case GoldenBonusSymbolTypes.BonusX2500:
				return ["bonus_2500_win", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_2500_win"];
			default:
				return undefined;
		}
	}

	public getRevealAnimationForSymbolType(symbolType: SymbolType): [string, SpineResource, string] | undefined {
		switch (symbolType) {
			case BaseSymbolTypes.LV1:
				return ["lv1", this.lvAnimation(), "lv"];
			case BaseSymbolTypes.LV2:
				return ["lv2", this.lvAnimation(), "lv"];
			case BaseSymbolTypes.LV3:
				return ["lv3", this.lvAnimation(), "lv"];
			case BaseSymbolTypes.LV4:
				return ["lv4", this.lvAnimation(), "lv"];
			case BaseSymbolTypes.HV1:
				return ["hv1", this.hv1Animation(), "default"];
			case BaseSymbolTypes.HV2:
				return ["hv2", this.hv2Animation(), "default"];
			case BaseSymbolTypes.HV3:
				return ["hv3", this.hv3Animation(), "default"];
			case BaseSymbolTypes.HV4:
				return ["hv4", this.hv4Animation(), "default"];
			case BaseSymbolTypes.Top:
				return ["top_mystery_expand", this.goldenBonusAssetsService.goldenTriggerSpine(), "top_mystery_expand"];
			case BaseSymbolTypes.Scatter:
			case BaseSymbolTypes.ScatterFG:
			case GoldenBonusSymbolTypes.BonusX1:
			case GoldenBonusSymbolTypes.BonusX2:
			case GoldenBonusSymbolTypes.BonusX5:
			case GoldenBonusSymbolTypes.BonusX10:
			case GoldenBonusSymbolTypes.BonusX25:
			case GoldenBonusSymbolTypes.BonusX50:
			case GoldenBonusSymbolTypes.BonusX100:
			case GoldenBonusSymbolTypes.BonusX250:
			case GoldenBonusSymbolTypes.BonusX500:
			case GoldenBonusSymbolTypes.BonusX1000:
			case GoldenBonusSymbolTypes.BonusX2500:
				return ["golden-bonus_expand", this.goldenBonusAssetsService.goldenTriggerSpine(), "golden-bonus_expand"];
			default:
				return undefined;
		}
	}

	public getRevealTextureForSymbolType(symbolType: SymbolType): TextureResource | undefined {
		switch (symbolType) {
			case BaseSymbolTypes.Top:
				return this.symbolTopCut();
			case BaseSymbolTypes.Scatter:
				return this.symbolTopGoldBig();
			case BaseSymbolTypes.ScatterFG:
				return this.symbolTopGoldBig();
			case GoldenBonusSymbolTypes.BonusX1:
			case GoldenBonusSymbolTypes.BonusX2:
			case GoldenBonusSymbolTypes.BonusX5:
			case GoldenBonusSymbolTypes.BonusX10:
			case GoldenBonusSymbolTypes.BonusX25:
			case GoldenBonusSymbolTypes.BonusX50:
			case GoldenBonusSymbolTypes.BonusX100:
			case GoldenBonusSymbolTypes.BonusX250:
			case GoldenBonusSymbolTypes.BonusX500:
			case GoldenBonusSymbolTypes.BonusX1000:
			case GoldenBonusSymbolTypes.BonusX2500:
				return this.symbolTopGold();
			default:
				return this.getTextureForSymbolType(symbolType);
		}
	}

	public getGoldenTeaserAnimation(symbolType: SymbolType): [string, SpineResource, string] | undefined {
		switch (symbolType) {
			case GoldenBonusSymbolTypes.BonusX1:
				return ["bonus_1_teaser", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_1_teaser"];
			case GoldenBonusSymbolTypes.BonusX2:
				return ["bonus_2_teaser", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_2_teaser"];
			case GoldenBonusSymbolTypes.BonusX5:
				return ["bonus_5_teaser", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_5_teaser"];
			case GoldenBonusSymbolTypes.BonusX10:
				return ["bonus_10_teaser", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_10_teaser"];
			case GoldenBonusSymbolTypes.BonusX25:
				return ["bonus_25_teaser", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_25_teaser"];
			case GoldenBonusSymbolTypes.BonusX50:
				return ["bonus_50_teaser", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_50_teaser"];
			case GoldenBonusSymbolTypes.BonusX100:
				return ["bonus_100_teaser", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_100_teaser"];
			case GoldenBonusSymbolTypes.BonusX250:
				return ["bonus_250_teaser", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_250_teaser"];
			case GoldenBonusSymbolTypes.BonusX500:
				return ["bonus_500_teaser", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_500_teaser"];
			case GoldenBonusSymbolTypes.BonusX1000:
				return ["bonus_1000_teaser", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_1000_teaser"];
			case GoldenBonusSymbolTypes.BonusX2500:
				return ["bonus_2500_teaser", this.goldenBonusAssetsService.bonusSymbolSpine(), "bonus_2500_teaser"];
			case BaseSymbolTypes.Scatter:
				return ["scatter_bg_teaser", this.scatterAnimation(), "scatter_bg_teaser"];
			case BaseSymbolTypes.ScatterFG:
				return ["scatter_fg_teaser", this.scatterAnimation(), "scatter_fg_teaser"];
			default:
				return undefined;
		}
	}

	public getUncoverAnimation(symbolType: SymbolType): [string, SpineResource, string] | undefined {
		return symbolType === BaseSymbolTypes.Mystery
			? ["mystery_nudging_out", this.mysteryAnimation(), "mystery_nudging_out"]
			: undefined;
	}

	/**
	 * Every symbol model carries the frame, also the mystery itself: the nudging overlay builds plain
	 * symbol views, and those assert on a metadata texture key without a value. The RevealSymbolView
	 * shows the frame only on an uncovered symbol.
	 */
	public getMysteryFrameTexture(): TextureResource {
		return this.symbolMysteryFrame();
	}

	public getCoverAnimation(symbolType: SymbolType): [string, SpineResource, string] | undefined {
		return symbolType === BaseSymbolTypes.Mystery
			? ["mystery_nudging_in", this.mysteryAnimation(), "mystery_nudging_in"]
			: undefined;
	}

	public getSymbolOversizeOffset(symbolType: string): Victor | undefined {
		let offset;
		switch (symbolType) {
			case BaseSymbolTypes.Top:
				offset = new Victor(0, -53);
				break;
			case BaseSymbolTypes.Scatter:
				offset = new Victor(0, -82);
				break;
			case BaseSymbolTypes.Mystery:
				offset = new Victor(0, -24);
				break;
			case BaseSymbolTypes.ScatterFG:
				offset = new Victor(0, -82);
				break;
		}

		// The calavera textures fit their cell, but the golden-bonus_expand animation grows out of it.
		// The offset keeps the texture where it is, and the symbol view moves to the oversized layer
		// of the reel, which draws over the neighbour cells and their frames.
		if (Object.values(GoldenBonusSymbolTypes).includes(symbolType as GoldenBonusSymbolTypes)) {
			offset = new Victor(0, 0);
		}
		return offset;
	}

	public getTeaserAddon(
		symbolType: string
	): { constructor: typeof TeaserAddon; options: ITeaserAnimationAddonOptions } | undefined {
		if (symbolType === BaseSymbolTypes.Mystery) {
			return {
				constructor: MysteryTeaserAddon,
				options: {
					animation: this.mysteryAnimation(),
					animationName: "mystery_teaser",
					symbolId: symbolType,
				} as ITeaserAnimationAddonOptions,
			};
		}
		if (symbolType === BaseSymbolTypes.Scatter) {
			return {
				constructor: TeaserAddon,
				options: {
					animation: this.scatterAnimation(),
					animationName: "scatter_bg_teaser",
					sounds: this.scatterTeaserSounds(),
					triggerAmount: 3,
					symbolId: symbolType,
				} as ITeaserAnimationAddonOptions,
			};
		}
		if (symbolType === BaseSymbolTypes.ScatterFG) {
			return {
				constructor: TeaserAddon,
				options: {
					animation: this.scatterAnimation(),
					animationName: "scatter_fg_teaser",
					sounds: this.scatterTeaserSounds(),
					triggerAmount: 1,
					symbolId: symbolType,
				} as ITeaserAnimationAddonOptions,
			};
		}
		return undefined;
	}

	public getNudgeAnimation(symbolType: SymbolType): [string, SpineResource, string] | undefined {
		return symbolType === BaseSymbolTypes.Mystery
			? ["mystery_nudging_down", this.mysteryAnimation(), "mystery_nudging_down"]
			: undefined;
	}

	public getBonusAnimationExtra(symbolType: SymbolType): [string, SpineResource] | undefined {
		switch (symbolType) {
			case BaseSymbolTypes.Scatter:
				return ["scatter_bg_+1", this.scatterAnimation()];
			case BaseSymbolTypes.ScatterFG:
				return ["scatter_fg_+1", this.scatterAnimation()];
			default:
				return undefined;
		}
	}

}
